import { useApplicationStore } from '@data';
import { useShallow } from 'zustand/shallow';
import { usePatientSubscriptions } from './usePatientSubscriptions';
import React from 'react';
import { serviceIsMht, serviceIsWeightLossOrUrgentCare } from '../utils';
import { useNavigate } from 'react-router-dom';
import { SERVICES_ROUTE_PATHNAME } from '@router/routes/routePathnames';

export function useMht() {
  const { patientSubscriptions, subscriptionsAvailableToPatient, loading } =
    usePatientSubscriptions();

  //  return the first subscription if it exists since we only have mht so all existing subs are mht
  // * NOTE: There is only one subscription currently and it is MHT. We have yet to build out how to handle
  //   identifying different subscriptions, but when we have multiple and the BE has a way to do so, this will need to be updated.
  // !! this will break if we add other subscriptions
  const availableMhtSubscription = subscriptionsAvailableToPatient?.[0];
  const patientsMhtSubscription = patientSubscriptions?.[0];

  const { isMht, isWeightLossOrUrgentCare /* , reservedVisit  */ } =
    useApplicationStore(
      useShallow((s) => {
        const serviceType = s.getServiceTypeByVisitTypeId(s.visitTypeId ?? '');
        const mhtServiceSelected = serviceIsMht(
          serviceType?.serviceTypeId ?? '',
        );

        const weightLossOrUrgentCareServiceSelected =
          serviceIsWeightLossOrUrgentCare(serviceType?.serviceTypeId ?? '');
        return {
          isMht: mhtServiceSelected,
          isWeightLossOrUrgentCare: weightLossOrUrgentCareServiceSelected,
          // reservedVisit: s.reservedVisit,
        };
      }),
    );

  const patientsActiveMhtSubscription =
    patientsMhtSubscription &&
    (!patientsMhtSubscription?.endDateUtc ||
      new Date(patientsMhtSubscription.endDateUtc) > new Date())
      ? patientsMhtSubscription
      : null;

  const patientHasActiveMhtSubscription = Boolean(
    patientsActiveMhtSubscription,
  );

  const patientsMostRecentMhtSubscriptionIsCancelled = Boolean(
    patientsMhtSubscription && !patientHasActiveMhtSubscription,
  );
  const isMhtResubscribe =
    isMht && patientsMostRecentMhtSubscriptionIsCancelled;

  const isNewMhtSubscription =
    isMht && !isMhtResubscribe && !patientHasActiveMhtSubscription;

  const validEntitlements = (
    patientsActiveMhtSubscription?.entitlements ?? []
  ).filter(
    (entitlement) =>
      entitlement.remainingQuantity > 0 &&
      (!entitlement.validUntilDateTimeUtc ||
        new Date(entitlement.validUntilDateTimeUtc) > new Date()),
  );

  const hasFreeVisitEntitlement = validEntitlements.find(
    (entitlement) => entitlement.price === 0,
  );

  const discountedVisitEntitlement = validEntitlements
    .filter((entitlement) => entitlement.price > 0)
    .sort((a, b) => a.price - b.price)?.[0];

  const hasNewSubscriptionFreeVisitAndFreeMonth =
    availableMhtSubscription?.hasFreeTrial && !patientsMhtSubscription;

  const isBookingFreeMhtVisit =
    isMht &&
    (hasFreeVisitEntitlement || hasNewSubscriptionFreeVisitAndFreeMonth);

  const navigate = useNavigate();

  React.useEffect(() => {
    if (
      isMht && // user logged in and subscriptions have been fetched
      Array.isArray(subscriptionsAvailableToPatient) &&
      // user has no subscriptions available
      !subscriptionsAvailableToPatient.length &&
      // patient subscriptions have been fetched
      Array.isArray(patientSubscriptions) &&
      // and user does not have an active MHT subscription
      !patientHasActiveMhtSubscription
    ) {
      // * NOTE: There is only one subscription currently and it is MHT. We have yet to build out how to handle
      //   identifying different subscriptions, but when we have multiple and the BE has a way to do so, this will need
      //   to be updated.

      navigate(SERVICES_ROUTE_PATHNAME);
    }
  }, [
    patientSubscriptions,
    isMht,
    subscriptionsAvailableToPatient,
    navigate,
    patientHasActiveMhtSubscription,
  ]);

  // Want to be explicit that this is temporary, FE calculated, and to differentiate from the actual api
  // subscriptionBasedPrice field which will replace it shortly
  const FRONT_END_CALCULATED_subscriptionBasedPrice = isMht
    ? hasFreeVisitEntitlement || hasNewSubscriptionFreeVisitAndFreeMonth
      ? 0
      : (discountedVisitEntitlement?.price ?? null)
    : isWeightLossOrUrgentCare && discountedVisitEntitlement
      ? discountedVisitEntitlement.price
      : null;

  return {
    isMht,
    isMhtResubscribe,
    isNewMhtSubscription,
    isSubscribingToMht: isNewMhtSubscription || isMhtResubscribe,
    patientHasActiveMhtSubscription,
    isBookingFreeMhtVisit,
    isBookingPaidMhtVisit: isMht && !isBookingFreeMhtVisit,
    patientsMostRecentMhtSubscriptionIsCancelled,
    patientsMhtSubscription,
    availableMhtSubscription,
    loading,
    FRONT_END_CALCULATED_subscriptionBasedPrice,
  };
}
